import React from 'react';
import { observer } from 'mobx-react';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import styles from './widgets.module.css';
import dashboardStore from '../../../stores/dashboard.store';
import memberStore from '../../../stores/member.store';
import Time from '../../../services/time.service';
import { HealthRates, statUnitLabel } from '../../../models/stat.model';

const crashFreeColor = (rate?: number) => {
  if (rate === undefined) return 'var(--brown-grey)';
  if (rate >= 0.995) return 'var(--tealish)';
  if (rate >= 0.98) return 'var(--mango)';
  return 'var(--lipstick)';
};

const formatRate = (rate?: number) =>
  rate === undefined ? '—' : `${(rate * 100).toFixed(2)}%`;

interface VersionRowProps {
  version: string;
  sessions: number;
  rates: HealthRates;
}

const VersionRow = ({ version, sessions, rates }: VersionRowProps) => (
  <tr>
    <td className={styles.healthVersionName}>{version || '(no version)'}</td>
    <td>{sessions.toLocaleString()}</td>
    <td style={{ color: crashFreeColor(rates.crashFreeSessions) }}>{formatRate(rates.crashFreeSessions)}</td>
    <td>{formatRate(rates.errorFreeSessions)}</td>
    <td>{formatRate(rates.warningFreeSessions)}</td>
  </tr>
);

interface TrendPoint {
  label: string;
  value?: number;
}

interface MiniTrendProps {
  title: string;
  color: string;
  strokeWidth?: number;
  data: TrendPoint[];
}

// One sparkline per rate, y-axis zoomed to the rate's own range — on a shared
// 0.5–1.0 axis a crash-free regression of 8x would move the line ~1px.
const MiniTrend = ({ title, color, strokeWidth = 1.5, data }: MiniTrendProps) => {
  const values = data.map(point => point.value).filter((value): value is number => value !== undefined);
  if (values.length === 0) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = Math.max((max - min) * 0.25, 0.0005);
  const domain: [number, number] = [Math.max(min - pad, 0), Math.min(max + pad, 1)];
  return (
    <div className={styles.healthTrend}>
      <div className={styles.healthTrendTitle} style={{ color }}>{title}</div>
      <ResponsiveContainer width="100%" height={84}>
        <LineChart data={data}>
          <Tooltip wrapperStyle={{ zIndex: 1 }} formatter={value => formatRate(Number(value))} />
          <XAxis dataKey="label" hide />
          <YAxis domain={domain} ticks={domain} width={46} tickFormatter={formatRate}
            tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
          <Line type="monotone" dataKey="value" name={title} stroke={color}
            strokeWidth={strokeWidth} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const HealthRatesWidget = observer(() => {
  const { dailyStats } = dashboardStore;

  if (!dailyStats) {
    return (
      <div>
        <div className={styles.chartTitle}> Health rates: </div>
        <div className={styles.healthEmpty}>No data yet</div>
      </div>
    );
  }

  const { overall, histogram, perVersion } = dailyStats;
  const unit = statUnitLabel(memberStore.app?.platform) === 'Requests' ? 'requests' : 'sessions';
  const trend = (key: 'crashFreeSessions' | 'errorFreeSessions' | 'warningFreeSessions'): TrendPoint[] =>
    histogram.map(entry => ({ label: new Time(entry.date).getShortDate(), value: entry[key] }));

  return (
    <div>
      <div className={styles.chartTitle}> Health rates · last {histogram.length} days: </div>
      <div className={styles.healthRates}>
        <div className={styles.healthHeadline}>
          <div className={styles.healthLabel}>Crash-free {unit}</div>
          <div className={styles.healthHeadlineValue} style={{ color: crashFreeColor(overall.crashFreeSessions) }}>
            {formatRate(overall.crashFreeSessions)}
          </div>
          <div className={styles.healthCounts}>
            {overall.sessions.toLocaleString()} {unit}
            {overall.devices !== undefined && ` · ${overall.devices.toLocaleString()} devices`}
            {overall.users !== undefined && overall.users > 0 && ` · ${overall.users.toLocaleString()} users`}
          </div>
        </div>
        <div className={styles.healthSecondary}>
          <div>
            <div className={styles.healthLabel}>Error-free</div>
            <div className={styles.healthSecondaryValue}>{formatRate(overall.errorFreeSessions)}</div>
          </div>
          <div>
            <div className={styles.healthLabel}>Warning-free</div>
            <div className={styles.healthSecondaryValue}>{formatRate(overall.warningFreeSessions)}</div>
          </div>
        </div>
        <div className={styles.healthTrends}>
          <MiniTrend title="Crash-free" color="#21a1b5" strokeWidth={3} data={trend('crashFreeSessions')} />
          <MiniTrend title="Error-free" color="#e51a41" data={trend('errorFreeSessions')} />
          <MiniTrend title="Warning-free" color="#ff8e1d" data={trend('warningFreeSessions')} />
        </div>
        {perVersion.length > 0 &&
          <div className={styles.healthVersions}>
            <table className={styles.healthVersionsTable}>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>{unit === 'requests' ? 'Requests' : 'Sessions'}</th>
                  <th>Crash-free</th>
                  <th>Error-free</th>
                  <th>Warning-free</th>
                </tr>
              </thead>
              <tbody>
                {perVersion.map(entry =>
                  <VersionRow key={entry.version} version={entry.version} sessions={entry.sessions} rates={entry} />)}
              </tbody>
            </table>
          </div>
        }
      </div>
    </div>
  );
});

export default HealthRatesWidget;
